<template>
    <div class="img-upload" :style="style">
        <button v-if="allowDelete" class="btn-x-file" type="button" @click="remove">
            <i translate="no" class="material-icons notranslate"> clear </i>
        </button>
        <button
            v-if="allowSelect"
            class="btn-upload-file"
            :class="allowSelect ? '' : 'unselectable'"
            @click="select"
        >
            <i translate="no" class="material-icons notranslate">
                {{ selectIcon }}
            </i>
        </button>
        <template v-if="!loading">
            <img
                v-if="isImage"
                class="image-preview"
                :src="src"
                @error="flagAsError"
                @touchend="openPreview"
                @click="openPreview"
            />
            <div
                v-else-if="!src"
                class="broken-link flex flex-col content-center items-center text-center"
            >
                <vx-basic-icon :size="iconSize" use-mdi icon="broken_image" />
                <span :style="spanStyles">No Resource Available</span>
            </div>
            <div
                v-else
                class="document-preview flex flex-col content-center items-center text-center w-full overflow-hidden"
                @click="download"
            >
                <vx-basic-icon icon="FileTextIcon" :size="iconSize" />
                <span :style="spanStyles" style="text-overflow: ellipsis">{{ filename }}</span>
            </div>
        </template>
        <vx-image-preview
            v-if="isPreviewable && previewActive"
            :active.sync="previewActive"
            :title="filename"
            :src="src"
        />
    </div>
</template>

<script>
import { guid } from '../../../plugins/helpers/regex_helpers'

export default {
    name: 'vx-resource',
    props: {
        resource: {
            type: [Object, String],
            required: true
        },
        imageSize: {
            type: [Number, String],
            default: '200px'
        },
        allowDelete: {
            type: Boolean,
            default: false
        },
        allowSelect: {
            type: Boolean,
            default: false
        },
        selectIcon: {
            type: String,
            default: 'cloud_upload'
        }
    },
    data() {
        return {
            localResource: null,
            error: false,
            previewActive: false
        }
    },
    watch: {
        resource: {
            handler() {
                this.parseResource(this.resource)
            },
            immediate: true
        },
        src: {
            handler() {
                this.error = false
            },
            immediate: true
        }
    },
    computed: {
        style() {
            return {
                height: `${this.imageSize} !important`,
                width: `${this.imageSize} !important`
            }
        },
        isPreviewable() {
            return this.isImage && !this.isError
        },
        filename() {
            return this.localResource && this.localResource.filename
        },
        src() {
            if (this.localResource && this.localResource.src) {
                return this.localResource.src
            }
            if (this.id) {
                return this.getUrl(this.id)
            }
            return null
        },
        mimeType() {
            return (this.localResource && this.localResource.mimeType) || ''
        },
        id() {
            return this.localResource && this.localResource.id
        },
        file() {
            return this.localResource && this.localResource.file
        },
        isImage() {
            return this.mimeType.startsWith('image')
        },
        hasFile() {
            return !!this.file
        },
        isNewUpload() {
            return this.hasFile
        },
        imageSizeInPx() {
            return this.getPxSize(this.imageSize)
        },
        spanStyles() {
            const baseSize = 70
            const baseFontSize = 10
            const scale = 5 / Math.log(200 / 70)
            const fontSize = baseFontSize + scale * Math.log(this.imageSizeInPx / baseSize)

            return {
                'font-size': fontSize + 'px'
            }
        },
        iconSize() {
            return this.imageSizeInPx / 10
        }
    },
    methods: {
        getPxSize(str) {
            // Get the numeric value and unit from imageSize
            const sizeValue = parseFloat(str)
            const unit = str.replace(sizeValue, '')

            const remValueInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize)

            // Compute the icon size based on the imageSize
            switch (unit) {
                case 'rem':
                    return sizeValue * remValueInPixels
                case '%':
                    var parentWidth = this.$parent.$el.clientWidth
                    return (sizeValue * parentWidth) / 100
                default: // Or some default value
                    return sizeValue
            }
        },
        openPreview() {
            this.previewActive = true
        },
        select() {
            if (this.allowSelect) {
                this.$emit('selected', this.resource)
            }
        },
        remove() {
            if (this.allowDelete || this.isNewUpload) {
                this.$emit('removed', {
                    original: this.resource,
                    actual: this.localResource
                })
            }
        },
        download() {
            if (this.hasFile) {
                this.file.download()
                return
            }
            try {
                this.$api.resources.download(this.id)
            } catch (err) {
                this.notifyServerError(err)
            }
        },
        flagAsError() {
            this.$nextTick(() => {
                this.error = true
            })
        },
        getUrl(id) {
            return this.$api.resources.getUrlFromGuid(id)
        },
        async parseResource(resource) {
            try {
                this.loading = true
                if (typeof resource === 'string') {
                    this.localResource = await this.parseStringResource(resource)
                    return
                }
                this.localResource = await this.parseObjectResource(resource)
            } finally {
                this.loading = false
            }
        },
        async parseStringResource(resourceString) {
            if (!guid.IS_VALID_REGEX.test(resourceString)) {
                return {
                    src: resourceString
                }
            }
            try {
                return await this.$api.resources.getMeta(resourceString)
            } catch (error) {
                this.flagAsError()
            }
        },
        async parseObjectResource(resource) {
            if (resource.mimeType || resource.filename) {
                return resource
            }

            if (resource.id) {
                return await this.parseStringResource(resource.id)
            }

            this.flagAsError()
        }
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.theme-dark {
    .img-upload {
        background: $content-dark-bg;
    }
}

.img-upload {
    aspect-ratio: 1;
    background: $grey-wash;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    position: relative;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-upload-file span {
    opacity: 1;
    position: absolute;
    top: 12%;
    padding: 20px;
    transition: all 0.2s ease;
    z-index: 50;
    border-radius: 50%;
    font-size: 1.5rem;
}
.unselectable {
    pointer-events: none;
}
.image-preview {
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    max-width: 100%;
    max-height: 100%;
    cursor: zoom-in;
    object-fit: contain; /* Add this line */
}
.document-preview {
    cursor: pointer;
}
.broken-link {
    cursor: not-allowed;
}
</style>
